import { EntityEmptyState } from '@pesto/features/src/EntityProfile/components/EntityEmptyState';
import { EntityErrorBanner } from '@pesto/features/src/EntityProfile/components/EntityErrorBanner';
import { LoadingCard } from '@pesto/ui';
import { Tabs, TabsList, TabsTrigger } from '@pesto/ui/components/Tabs';
import { SmartTable } from '@pesto/ui/features/SmartTable/SmartTable';
import { Receipt } from 'lucide-react';
import { useMemo, useState } from 'react';

import { TransactionViewMode, useGetLoanTransactionsQuery } from '../../../../__generated__/graphql/api';
import {
  buildClientTransactionMatchKey,
  toUserTransactionListItem,
  type UserTransactionListItem,
} from './toUserTransactionListItem';
import { useUserTransactionsListConfig } from './useUserTransactionsListConfig';

interface LoanTransactionsTabProps {
  loanId: string;
}

export const LoanTransactionsTab = ({ loanId }: LoanTransactionsTabProps) => {
  const [viewMode, setViewMode] = useState<TransactionViewMode>(TransactionViewMode.Client);

  const {
    data: clientData,
    loading: clientLoading,
    error: clientError,
  } = useGetLoanTransactionsQuery({
    variables: { loanId, viewMode: TransactionViewMode.Client },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: ledgerData,
    loading: ledgerLoading,
    error: ledgerError,
  } = useGetLoanTransactionsQuery({
    variables: { loanId, viewMode: TransactionViewMode.Ledger },
    skip: viewMode !== TransactionViewMode.Ledger,
    notifyOnNetworkStatusChange: true,
  });

  const { getColumns, initialTableState } = useUserTransactionsListConfig();
  const error = clientError ?? ledgerError;
  const loading = viewMode === TransactionViewMode.Client ? clientLoading : ledgerLoading || clientLoading;

  const rows: UserTransactionListItem[] = useMemo(() => {
    if (viewMode === TransactionViewMode.Client) {
      return (clientData?.loanTransactions?.transactions ?? [])
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
        .map((txn) => toUserTransactionListItem(txn));
    }

    const clientKeys = new Set(
      (clientData?.loanTransactions?.transactions ?? [])
        .filter((t): t is NonNullable<typeof t> => Boolean(t))
        .map(buildClientTransactionMatchKey),
    );

    return (ledgerData?.loanTransactions?.transactions ?? [])
      .filter((t): t is NonNullable<typeof t> => Boolean(t))
      .map((txn) =>
        toUserTransactionListItem(txn, { ledgerOnly: !clientKeys.has(buildClientTransactionMatchKey(txn)) }),
      );
  }, [viewMode, clientData, ledgerData]);

  const onValueChange = (next: string) => {
    if (next === TransactionViewMode.Client || next === TransactionViewMode.Ledger) {
      setViewMode(next);
    }
  };

  const viewToggle = (
    <Tabs value={viewMode} onValueChange={onValueChange} className="w-fit">
      <TabsList>
        <TabsTrigger value={TransactionViewMode.Client}>Client View</TabsTrigger>
        <TabsTrigger value={TransactionViewMode.Ledger}>Ledger View</TabsTrigger>
      </TabsList>
    </Tabs>
  );

  if (loading && rows.length === 0) {
    return (
      <div className="space-y-4">
        {viewToggle}
        <LoadingCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {viewToggle}
        <EntityErrorBanner message="We couldn't load transactions." detail={error.message} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-4">
        {viewToggle}
        <EntityEmptyState icon={Receipt} title="This customer has no transactions on file." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {viewToggle}
      <SmartTable
        title="Transactions"
        rows={rows}
        columns={getColumns()}
        loading={loading}
        initialState={initialTableState}
      />
    </div>
  );
};
